/**
 * Shared Playwright click helpers for the direct client.
 *
 * @module playwright-client/click-actions
 */

import type { Locator, Page } from 'playwright';
import { HUMAN_CLICK_PRESS_DELAY_MS } from '../click-timing.js';

type ClickButton = 'left' | 'right' | 'middle';
type ClickModifier = 'Alt' | 'Control' | 'ControlOrMeta' | 'Meta' | 'Shift';

const CLICK_MODIFIERS = new Set<string>(['Alt', 'Control', 'ControlOrMeta', 'Meta', 'Shift']);
const CLICK_MODIFIER_ALIASES = new Map<string, ClickModifier>([
  ['alt', 'Alt'],
  ['option', 'Alt'],
  ['control', 'Control'],
  ['ctrl', 'Control'],
  ['controlormeta', 'ControlOrMeta'],
  ['control-or-meta', 'ControlOrMeta'],
  ['ctrlormeta', 'ControlOrMeta'],
  ['cmdorctrl', 'ControlOrMeta'],
  ['meta', 'Meta'],
  ['cmd', 'Meta'],
  ['command', 'Meta'],
  ['shift', 'Shift'],
]);

export interface LocatorClickOptionsInput {
  button?: string;
  modifiers?: string[];
  timeout: number;
}

export interface MouseClickOptionsInput {
  doubleClick?: boolean;
}

export interface LocatorClickOptions {
  button?: ClickButton;
  modifiers?: ClickModifier[];
  timeout: number;
  delay: number;
}

export function buildLocatorClickOptions(opts: LocatorClickOptionsInput): LocatorClickOptions {
  return {
    button: opts.button as ClickButton | undefined,
    modifiers: normalizeClickModifiers(opts.modifiers),
    timeout: opts.timeout,
    delay: HUMAN_CLICK_PRESS_DELAY_MS,
  };
}

export function normalizeClickModifiers(modifiers?: string[]): ClickModifier[] | undefined {
  if (!modifiers?.length) return undefined;
  return modifiers.map(normalizeClickModifier);
}

function normalizeClickModifier(modifier: string): ClickModifier {
  const trimmed = modifier.trim();
  const alias = CLICK_MODIFIER_ALIASES.get(trimmed.toLowerCase());
  if (alias) return alias;
  if (isClickModifier(trimmed)) return trimmed;

  throw new Error(
    `Invalid click modifier "${modifier}". Expected one of Alt, Control, ControlOrMeta, Meta, Shift.`
  );
}

function isClickModifier(value: string): value is ClickModifier {
  return CLICK_MODIFIERS.has(value);
}

export async function clickLocatorWithOptions(
  locator: Locator,
  options: LocatorClickOptions,
  opts?: { doubleClick?: boolean }
): Promise<void> {
  if (opts?.doubleClick) {
    await locator.dblclick(options);
  } else {
    await locator.click(options);
  }
}

export async function clickMouseWithDelay(
  page: Page,
  x: number,
  y: number,
  opts?: MouseClickOptionsInput
): Promise<void> {
  if (opts?.doubleClick) {
    await page.mouse.dblclick(x, y, { delay: HUMAN_CLICK_PRESS_DELAY_MS });
  } else {
    await page.mouse.click(x, y, { delay: HUMAN_CLICK_PRESS_DELAY_MS });
  }
}

export async function pressClickModifiers(page: Page, modifiers?: string[]): Promise<void> {
  for (const mod of normalizeClickModifiers(modifiers) ?? []) {
    await page.keyboard.down(mod);
  }
}

export async function releaseClickModifiers(page: Page, modifiers?: string[]): Promise<void> {
  for (const mod of normalizeClickModifiers(modifiers) ?? []) {
    await page.keyboard.up(mod);
  }
}
